import { LiveStore, NamedNode, st, sym } from 'rdflib'
import { html, render } from 'lit-html'
import { ns } from 'solid-ui'
import 'solid-ui/components/button'
import { editIcon, plusIcon, trashIcon } from './icons'
import { createSpinner } from './spinner'
/* The following code was generated by AI Model: GPT-5.4
   Prompt: can you create me a form to enter this data [
ns.foaf('homepage'),
ns.foaf('weblog'),
ns.foaf('workplaceHomepage'),
ns.foaf('schoolHomepage')
] and put an edit button at the top left corner of the header
 that when you click it pops up so you can enter this information
 and save it to the Webid. You can look in
 Dev2025/profile-pane/src/sections/heading/mutations to see how
 to write this data in the profile. */
type SolidUIButton = HTMLElement & {
  type: string
  variant: string
  size: string
  label: string
  disabled: boolean
}

type ProfileLinkField = {
  key: 'homepage' | 'weblog' | 'workplaceHomepage' | 'schoolHomepage'
  label: string
  placeholder: string
  predicate: NamedNode
}

type ProfileLinksValues = Record<ProfileLinkField['key'], string[]>

type EditProfileDetailsButtonOptions = {
  dom: HTMLDocument
  store: LiveStore
  subject: NamedNode
  onSaved: () => void
}

export const profileLinkFields: ProfileLinkField[] = [
  {
    key: 'homepage',
    label: 'Homepage',
    placeholder: 'https://example.com',
    predicate: ns.foaf('homepage')
  },
  {
    key: 'weblog',
    label: 'Weblog',
    placeholder: 'https://blog.example.com',
    predicate: ns.foaf('weblog')
  },
  {
    key: 'workplaceHomepage',
    label: 'Workplace Homepage',
    placeholder: 'https://company.example.com',
    predicate: ns.foaf('workplaceHomepage')
  },
  {
    key: 'schoolHomepage',
    label: 'School Homepage',
    placeholder: 'https://school.example.edu',
    predicate: ns.foaf('schoolHomepage')
  }
]

function createEmptyProfileLinkValues (): ProfileLinksValues {
  return {
    homepage: [''],
    weblog: [''],
    workplaceHomepage: [''],
    schoolHomepage: ['']
  }
}

function readProfileLinkValues (store: LiveStore, subject: NamedNode): ProfileLinksValues {
  const doc = subject.doc()
  return profileLinkFields.reduce((result, field) => {
    const values = store
      .statementsMatching(subject, field.predicate, undefined, doc)
      .map((statement) => statement.object?.value || '')
      .filter(Boolean)

    result[field.key] = values.length ? values : ['']
    return result
  }, createEmptyProfileLinkValues())
}

function normalizeUrlList (values: string[], fieldLabel: string): string[] {
  const unique = new Set<string>()
  const lines = values
    .map((entry) => entry.trim())
    .filter(Boolean)

  for (const line of lines) {
    let parsed: URL
    try {
      parsed = new URL(line)
    } catch {
      throw new Error(`${fieldLabel} must contain valid absolute URLs.`)
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error(`${fieldLabel} only supports http and https URLs.`)
    }

    unique.add(parsed.toString())
  }

  return [...unique]
}

async function saveProfileLinkValues (
  store: LiveStore,
  subject: NamedNode,
  values: ProfileLinksValues
): Promise<void> {
  const updater = (store as any).updater
  const fetcher = (store as any).fetcher
  if (!updater || typeof updater.serialize !== 'function' || !fetcher || typeof fetcher.webOperation !== 'function') {
    throw new Error('This profile cannot be edited with the current store.')
  }

  const doc = subject.doc()
  const insertions: any[] = []
  const existingStatements: any[] = []

  for (const field of profileLinkFields) {
    existingStatements.push(...store.statementsMatching(subject, field.predicate, undefined, doc))
    const nextValues = normalizeUrlList(values[field.key], field.label)
    for (const nextValue of nextValues) {
      insertions.push(st(subject, field.predicate, sym(nextValue), doc))
    }
  }

  const retainedStatements = store
    .statementsMatching(undefined, undefined, undefined, doc)
    .filter((statement) => {
      return !profileLinkFields.some((field) => {
        return statement.subject.equals(subject) && statement.predicate.equals(field.predicate)
      })
    })

  const nextStatements = retainedStatements.concat(insertions)
  const contentType = 'text/turtle'
  const body = updater.serialize(doc.value, nextStatements, contentType)
  const response = await fetcher.webOperation('PUT', doc.value, {
    noMeta: true,
    contentType,
    body
  })

  if (!response || response.ok !== true) {
    const status = response?.status || 'unknown'
    throw new Error(`Unable to save profile links. Web error: ${status}`)
  }

  existingStatements.forEach((statement) => {
    store.removeStatement(statement)
  })

  for (const statement of insertions) {
    store.add(statement.subject, statement.predicate, statement.object, statement.why)
  }
}

export function appendProfileLinks (
  _container: HTMLElement,
  _dom: HTMLDocument,
  _store: LiveStore,
  _subject: NamedNode
): void {
  // Hidden for now because social-pane website links are not part of the new design.
  // Revisit later if we decide to show profile links in this header again.
}

export function createEditProfileDetailsButton (
  options: EditProfileDetailsButtonOptions
): HTMLElement {
  const { dom, store, subject, onSaved } = options
  const button = dom.createElement('solid-ui-button') as SolidUIButton
  button.type = 'button'
  button.className = 'social-pane__edit-button profile__action-button profile__heading-action-button profile-action-text flex-center profile-section-collapsible__edit-button'
  button.variant = 'secondary'
  button.size = 'sm'

  const label = dom.createElement('span')
  label.className = 'profile-section-collapsible__edit-label social-pane__edit-button-label'
  label.innerHTML = `${editIcon} Edit`
  button.appendChild(label)

  const icon = dom.createElement('span')
  icon.className = 'profile-section-collapsible__edit-icon social-pane__edit-button-icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.innerHTML = editIcon
  button.appendChild(icon)

  button.setAttribute('aria-label', 'Edit profile links')

  const openDialog = () => {
    const initialValues = readProfileLinkValues(store, subject)
    const formState = profileLinkFields.reduce((result, field) => {
      result[field.key] = [...initialValues[field.key]]
      return result
    }, createEmptyProfileLinkValues())
    const overlay = dom.createElement('div')
    overlay.classList.add('social-pane__dialog-backdrop', 'flex-center')

    const dialog = dom.createElement('div')
    dialog.classList.add('social-pane__dialog')
    dialog.setAttribute('role', 'dialog')
    dialog.setAttribute('aria-modal', 'true')
    dialog.setAttribute('aria-label', 'Edit profile links')
    overlay.appendChild(dialog)

    const dialogHeader = dom.createElement('div')
    dialogHeader.classList.add('social-pane__dialog-header')
    dialog.appendChild(dialogHeader)

    const title = dom.createElement('h2')
    title.className = 'social-pane__dialog-title'
    title.textContent = 'Edit profile links'
    dialogHeader.appendChild(title)

    const form = dom.createElement('form')
    form.classList.add('social-pane__dialog-form', 'flex-column')
    dialog.appendChild(form)

    const savingOverlay = dom.createElement('div')
    savingOverlay.className = 'social-pane__dialog-saving-overlay'
    savingOverlay.hidden = true
    dialog.appendChild(savingOverlay)

    const fieldsContainer = dom.createElement('div')
    fieldsContainer.className = 'social-pane__dialog-fields'
    form.appendChild(fieldsContainer)

    const errorMessage = dom.createElement('p')
    errorMessage.className = 'social-pane__dialog-error'

    const focusRow = (fieldKey: ProfileLinkField['key'], rowIndex: number) => {
      const nextField = fieldsContainer.querySelector<HTMLInputElement>(`input[data-profile-link-key="${fieldKey}"][data-profile-link-index="${String(rowIndex)}"]`)
      nextField?.focus()
    }

    const renderFields = (focusTarget?: { fieldKey: ProfileLinkField['key'], rowIndex: number }) => {
      fieldsContainer.replaceChildren()

      for (const field of profileLinkFields) {
        const fieldWrapper = dom.createElement('section')
        fieldWrapper.classList.add('social-pane__dialog-field', 'flex-column')

        const fieldHeader = dom.createElement('div')
        fieldHeader.className = 'social-pane__dialog-field-header'
        fieldWrapper.appendChild(fieldHeader)

        const fieldLabel = dom.createElement('h3')
        fieldLabel.className = 'social-pane__dialog-label'
        fieldLabel.textContent = field.label
        fieldHeader.appendChild(fieldLabel)

        const addButton = dom.createElement('solid-ui-button') as SolidUIButton
        addButton.type = 'button'
        addButton.className = 'social-pane__dialog-row-button--add'
        addButton.setAttribute('aria-label', `Add another ${field.label.toLowerCase()}`)
        addButton.variant = 'secondary'
        addButton.size = 'sm'
        const addButtonContent = dom.createElement('span')
        addButtonContent.className = 'social-pane__add-more-content'
        const addButtonInline = dom.createElement('span')
        addButtonInline.className = 'social-pane__add-more-inline'
        const addButtonIcon = dom.createElement('span')
        addButtonIcon.className = 'social-pane__add-more-icon'
        addButtonIcon.setAttribute('aria-hidden', 'true')
        addButtonIcon.innerHTML = plusIcon
        const addButtonText = dom.createElement('span')
        addButtonText.textContent = 'Add More'
        addButtonInline.appendChild(addButtonIcon)
        addButtonInline.appendChild(addButtonText)
        addButtonContent.appendChild(addButtonInline)
        addButton.appendChild(addButtonContent)
        addButton.addEventListener('click', () => {
          formState[field.key].push('')
          renderFields({ fieldKey: field.key, rowIndex: formState[field.key].length - 1 })
        })
        fieldHeader.appendChild(addButton)

        const rows = dom.createElement('div')
        rows.className = 'social-pane__dialog-row-list'
        fieldWrapper.appendChild(rows)

        const values = formState[field.key].length ? formState[field.key] : ['']
        values.forEach((value, index) => {
          const row = dom.createElement('div')
          row.className = 'social-pane__dialog-row'

          const input = dom.createElement('input')
          input.classList.add('social-pane__dialog-input', 'input')
          input.type = 'url'
          input.placeholder = field.placeholder
          input.value = value
          input.setAttribute('data-profile-link-key', field.key)
          input.setAttribute('data-profile-link-index', String(index))
          input.addEventListener('input', () => {
            formState[field.key][index] = input.value
          })
          row.appendChild(input)

          const removeButton = dom.createElement('solid-ui-button') as SolidUIButton
          removeButton.type = 'button'
          removeButton.className = 'social-pane__dialog-row-button--remove'
          removeButton.setAttribute('aria-label', `Remove ${field.label.toLowerCase()} ${index + 1}`)
          removeButton.setAttribute('title', 'Remove')
          removeButton.variant = 'icon'
          removeButton.size = 'md'
          const removeButtonIcon = dom.createElement('span')
          removeButtonIcon.className = 'social-pane__dialog-row-button-icon social-pane__dialog-row-button-icon--remove'
          removeButtonIcon.setAttribute('slot', 'icon')
          removeButtonIcon.setAttribute('aria-hidden', 'true')
          removeButtonIcon.innerHTML = trashIcon
          removeButton.appendChild(removeButtonIcon)
          removeButton.addEventListener('click', () => {
            if (formState[field.key].length === 1) {
              formState[field.key][0] = ''
            } else {
              formState[field.key].splice(index, 1)
            }
            renderFields()
          })
          row.appendChild(removeButton)

          rows.appendChild(row)
        })

        fieldsContainer.appendChild(fieldWrapper)
      }

      if (focusTarget) {
        focusRow(focusTarget.fieldKey, focusTarget.rowIndex)
      }
    }

    renderFields()

    form.appendChild(errorMessage)

    const actions = dom.createElement('div')
    actions.className = 'social-pane__dialog-actions'
    form.appendChild(actions)

    const closeButton = dom.createElement('solid-ui-button') as SolidUIButton
    closeButton.type = 'button'
    closeButton.className = 'social-pane__dialog-button--cancel'
    closeButton.setAttribute('aria-label', 'Close dialog')
    closeButton.label = 'Close'
    closeButton.variant = 'secondary'
    closeButton.size = 'md'
    actions.appendChild(closeButton)

    const saveButton = dom.createElement('solid-ui-button') as SolidUIButton
    saveButton.type = 'button'
    saveButton.className = 'social-pane__dialog-button--save'
    saveButton.label = 'Save Changes'
    saveButton.variant = 'primary'
    saveButton.size = 'md'
    actions.appendChild(saveButton)

    let isSaving = false

    const setSavingState = (nextSaving: boolean) => {
      isSaving = nextSaving
      dialog.classList.toggle('modal--saving', nextSaving)
      dialog.setAttribute('aria-busy', String(nextSaving))
      form.toggleAttribute('inert', nextSaving)
      saveButton.disabled = nextSaving
      saveButton.setAttribute('aria-busy', String(nextSaving))
      closeButton.disabled = nextSaving
      savingOverlay.hidden = !nextSaving

      if (nextSaving) {
        const activeElement = dialog.ownerDocument.activeElement as HTMLElement | null
        if (activeElement && dialog.contains(activeElement)) {
          activeElement.blur()
        }

        render(html`
          <div class="modal__saving-indicator inline-flex-row justify-center" aria-live="polite" aria-label="Saving changes">
            ${createSpinner()}
          </div>
        `, savingOverlay)
      } else {
        render(html``, savingOverlay)
      }
    }

    const closeDialog = (force = false) => {
      if (isSaving && !force) return
      overlay.remove()
    }

    const requestCloseDialog = () => {
      closeDialog()
    }

    const handleSave = async () => {
      errorMessage.textContent = ''
      setSavingState(true)

      const nextValues = profileLinkFields.reduce((result, field) => {
        result[field.key] = [...formState[field.key]]
        return result
      }, createEmptyProfileLinkValues())

      try {
        await saveProfileLinkValues(store, subject, nextValues)
        setSavingState(false)
        closeDialog(true)
        onSaved()
      } catch (error) {
        errorMessage.textContent = error instanceof Error ? error.message : 'Unable to save profile links.'
      } finally {
        if (isSaving) {
          setSavingState(false)
        }
      }
    }

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closeDialog()
    })
    closeButton.addEventListener('click', requestCloseDialog)

    saveButton.addEventListener('click', async (event) => {
      event.preventDefault()
      await handleSave()
    })

    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDialog()
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()
    })

    dom.body.appendChild(overlay)
    focusRow('homepage', 0)
  }

  button.addEventListener('click', openDialog)
  return button
}
